import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Users, UserPlus, Search, Trash2, ToggleLeft, ToggleRight, X, Eye } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/utils/api";
import { toast } from "sonner";

export default function ManageUsers({ userRole = "customer" }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: userRole === "agent" ? "Agent@123" : "Customer@123" });
  const [saving, setSaving] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => { fetchUsers(); }, [userRole]);

  // Debounced search on every keystroke (customer role uses v2 profile search)
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUsers(search);
    }, 300);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [search]);

  const fetchUsers = async (q = "") => {
    setLoading(true);
    try {
      if (userRole === "customer") {
        // Use v2 customer-profiles search
        const params = {};
        if (q) params.search = q;
        const res = await api.get("/v2/customers/profiles", { params });
        const items = (res.data?.items || []).map((p) => ({
          id: p.customer_id || p.id || p._id,
          name: p.contact_person || p.company_name,
          email: p.email,
          phone: p.mobile,
          wallet_balance: p.wallet_balance || 0,
          customer_type: p.customer_type || "RETAIL",
          is_active: p.is_active !== false,
          created_at: p.created_at,
        }));
        setUsers(items);
      } else {
        const params = { role: userRole };
        if (q) params.search = q;
        const res = await api.get("/users", { params });
        setUsers(res.data || []);
      }
    } catch { /* ignore */ }
    finally { setLoading(false); }
  };

  const clearSearch = () => {
    setSearch("");
    fetchUsers("");
  };

  const toggleActive = async (user) => {
    try {
      await api.put(`/users/${user.id}`, { is_active: !user.is_active });
      toast.success(`User ${user.is_active ? "disabled" : "enabled"}`);
      fetchUsers(search);
    } catch {
      toast.error("Failed to update user status.");
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Delete ${user.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${user.id}`);
      toast.success("User deleted.");
      fetchUsers(search);
    } catch {
      toast.error("Failed to delete user.");
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/users", { ...form, role: userRole });
      toast.success(`${userRole === "agent" ? "Agent" : "Customer"} created!`);
      setShowAdd(false);
      setForm({ name: "", email: "", phone: "", password: userRole === "agent" ? "Agent@123" : "Customer@123" });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create user.");
    } finally {
      setSaving(false);
    }
  };

  const title = userRole === "agent" ? "Agents" : "Customers";

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900 uppercase">Manage {title}</h1>
            <p className="text-slate-500 text-sm">{users.length} {title.toLowerCase()} registered</p>
          </div>
          <button onClick={() => (userRole === "customer" ? navigate("/admin/customers/new") : setShowAdd(true))} data-testid={`add-${userRole}-btn`}
            className="flex items-center gap-2 px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 uppercase">
            <UserPlus className="w-4 h-4" /> Add {userRole === "agent" ? "Agent" : "Customer"}
          </button>
        </div>

        <form onSubmit={(e) => { e.preventDefault(); fetchUsers(search); }} className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${title.toLowerCase()}...`}
              data-testid={`${userRole}-search`}
              className="w-full pl-9 pr-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
          </div>
          <button type="submit" className="px-4 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm">Search</button>
          {search && (
            <button type="button" onClick={clearSearch} data-testid={`${userRole}-clear`}
              className="px-3 py-2.5 border border-slate-200 text-slate-600 text-sm rounded-sm">Clear</button>
          )}
        </form>

        <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-slate-400">Loading...</div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center"><Users className="w-12 h-12 text-slate-200 mx-auto mb-3" /><p className="text-slate-500">No {title.toLowerCase()} found.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    {(userRole === "customer"
                      ? ["Name", "Email", "Phone", "Wallet", "Type", "Status", "Joined", "Actions"]
                      : ["Name", "Email", "Phone", "Wallet", "Status", "Joined", "Actions"]
                    ).map((h) => (
                      <th key={h} className="px-3 py-2 text-xs font-bold text-slate-500 uppercase text-left">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {users.map((u) => (
                    <tr key={u.id} data-testid={`${userRole}-row`} className="hover:bg-slate-50">
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-brand-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">{u.name?.[0]}</div>
                          <span className="font-semibold text-sm text-slate-800">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-500">{u.email}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{u.phone || "—"}</td>
                      <td className="px-3 py-2 text-xs font-semibold text-slate-700">₹{(u.wallet_balance || 0).toFixed(2)}</td>
                      {userRole === "customer" && (
                        <td className="px-3 py-2">
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-100">
                            {u.customer_type || "RETAIL"}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${u.is_active ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-600"}`}>
                          {u.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-400">{u.created_at ? new Date(u.created_at).toLocaleDateString("en-IN") : "—"}</td>
                      <td className="px-3 py-2">
                        <div className="flex items-center gap-2">
                          {userRole === "customer" && (
                            <Link to={`/admin/customers/${u.id}`} data-testid={`view-${u.id}`}
                              className="p-1.5 text-slate-400 hover:text-brand-500 transition-colors rounded">
                              <Eye className="w-4 h-4" />
                            </Link>
                          )}
                          <button onClick={() => toggleActive(u)} data-testid={`toggle-${u.id}`}
                            className="p-1.5 text-slate-400 hover:text-brand-500 transition-colors rounded">
                            {u.is_active ? <ToggleRight className="w-4 h-4 text-emerald-500" /> : <ToggleLeft className="w-4 h-4" />}
                          </button>
                          <button onClick={() => handleDelete(u)} data-testid={`delete-${u.id}`}
                            className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded">
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl w-full max-w-md p-6 shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-heading font-bold text-lg uppercase">Add {userRole === "agent" ? "Agent" : "Customer"}</h3>
              <button onClick={() => setShowAdd(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            <form onSubmit={handleCreate} className="space-y-4">
              {[
                { name: "name", label: "Full Name", required: true },
                { name: "email", label: "Email", type: "email", required: true },
                { name: "phone", label: "Phone" },
                { name: "password", label: "Password", required: true },
              ].map((f) => (
                <div key={f.name}>
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5">{f.label}</label>
                  <input type={f.type || "text"} name={f.name} value={form[f.name]} required={f.required}
                    onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                    data-testid={`add-${userRole}-${f.name}`}
                    className="w-full px-3 py-2.5 border border-slate-200 rounded-sm text-sm focus:outline-none focus:border-brand-500" />
                </div>
              ))}
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowAdd(false)} className="flex-1 py-2.5 border border-slate-200 text-slate-600 text-sm font-semibold rounded-sm">Cancel</button>
                <button type="submit" disabled={saving} data-testid={`add-${userRole}-submit`}
                  className="flex-1 py-2.5 bg-brand-500 text-white text-sm font-semibold rounded-sm hover:bg-brand-600 disabled:opacity-70">
                  {saving ? "Creating..." : "Create"}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </DashboardLayout>
  );
}
